<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-radioactive me-2"></i>{{ $t('page.radiology', 'Radiology') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-blue" @click="openTemplates">
            <i class="mdi mdi-file-document-multiple-outline me-1"></i>Report Templates
          </button>
          <button class="hms-btn hms-btn-primary" @click="openAdd">
            <i class="mdi mdi-plus me-1"></i>New Request
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filterStatus=''" :class="{ selected: !filterStatus }">
          <div class="hms-stat-num">{{ requests.length }}</div>
          <div class="hms-stat-lbl">All Requests</div>
        </div>
        <div class="hms-stat" v-for="s in ['pending','scheduled','performed','reported']" :key="s" @click="filterStatus = (filterStatus === s ? '' : s)" :class="{ selected: filterStatus === s }">
          <div class="hms-stat-num" :class="statusColor(s)">{{ countByStatus(s) }}</div>
          <div class="hms-stat-lbl">{{ s.charAt(0).toUpperCase() + s.slice(1) }}</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-red">{{ statCount }}</div>
          <div class="hms-stat-lbl">STAT / Urgent</div>
        </div>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="search" placeholder="Search request #, patient, scan, body part..." />
          </div>
          <select class="hms-input hms-filter" v-model="filterPriority">
            <option value="">All priority</option>
            <option value="routine">Routine</option>
            <option value="urgent">Urgent</option>
            <option value="stat">STAT</option>
          </select>
          <span class="hms-muted ms-auto">{{ filteredRequests.length }} requests</span>
        </div>

        <div v-if="filteredRequests.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Request #</th>
              <th>Patient</th>
              <th>Scan</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Requested</th>
              <th>Actions</th>
            </tr></thead>
            <tbody>
              <tr v-for="r in filteredRequests" :key="r.radiology_id">
                <td>
                  <div class="hms-mono fw-semibold">{{ r.request_number }}</div>
                  <div class="hms-muted-sm">KSh {{ formatNumber(r.price) }}</div>
                </td>
                <td>
                  <div class="hms-text fw-semibold">{{ r.patient_first }} {{ r.patient_last }}</div>
                  <div class="hms-muted-sm hms-mono">{{ r.patient_number }}</div>
                </td>
                <td>
                  <div class="hms-text">{{ r.scan_type }}</div>
                  <div class="hms-muted-sm">{{ r.body_part || '—' }}{{ r.contrast_required == 1 ? ' · with contrast' : '' }}</div>
                </td>
                <td><span class="hms-badge" :class="priorityBadge(r.priority)">{{ r.priority }}</span></td>
                <td><span class="hms-badge" :class="statusBadge(r.status)">{{ r.status }}</span></td>
                <td>
                  <div class="hms-muted-sm">{{ formatDate(r.created_at) }}</div>
                  <div class="hms-muted-sm" v-if="r.scheduled_date"><i class="mdi mdi-calendar-clock"></i> {{ formatDateTime(r.scheduled_date) }}</div>
                </td>
                <td class="d-flex gap-1 flex-wrap">
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="openDetail(r)" title="View"><i class="mdi mdi-eye"></i></button>
                  <button v-if="r.status === 'pending'" class="hms-btn hms-btn-sm hms-btn-amber" @click="openSchedule(r)" title="Schedule"><i class="mdi mdi-calendar-plus"></i></button>
                  <button v-if="r.status === 'pending' || r.status === 'scheduled'" class="hms-btn hms-btn-sm hms-btn-info" @click="openPerform(r)" title="Mark performed"><i class="mdi mdi-check-decagram"></i></button>
                  <button v-if="r.status === 'performed'" class="hms-btn hms-btn-sm hms-btn-green" @click="openReport(r)" title="Write report"><i class="mdi mdi-file-document-edit"></i></button>
                  <button v-if="r.status !== 'cancelled' && r.status !== 'reported'" class="hms-btn hms-btn-sm hms-btn-danger" @click="cancelRequest(r)" title="Cancel"><i class="mdi mdi-close-circle"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-radioactive"></i>No radiology requests match.</div>
      </div>

      <!-- ADD MODAL -->
      <div class="hms-modal-overlay" v-if="showAdd" @click.self="showAdd=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-radioactive"></i> New Radiology Request</span>
            <button class="hms-modal-close" @click="showAdd=false">&times;</button>
          </div>
          <div v-if="formError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ formError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-12"><label class="hms-label">Patient *</label>
              <select class="hms-input" v-model="form.patient_id">
                <option value="">— Select patient —</option>
                <option v-for="p in patients" :key="p.patient_id" :value="p.patient_id">
                  {{ p.first_name }} {{ p.last_name }} · {{ p.patient_number }}
                </option>
              </select>
            </div>
            <div class="col-md-7"><label class="hms-label">Scan Type *</label>
              <select class="hms-input" v-model="form.scan_type">
                <option value="">— Select scan —</option>
                <option v-for="s in scanTypes" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <div class="col-md-5"><label class="hms-label">Body Part</label>
              <input class="hms-input" v-model="form.body_part" placeholder="e.g. Chest, Right knee" />
            </div>
            <div class="col-md-6"><label class="hms-label">Priority</label>
              <select class="hms-input" v-model="form.priority">
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="stat">STAT</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Price (KSh)</label>
              <input type="number" class="hms-input" v-model.number="form.price" min="0" step="50" />
            </div>
            <div class="col-md-6"><label class="hms-label">Scheduled Date (optional)</label>
              <input type="datetime-local" class="hms-input" v-model="form.scheduled_date" />
            </div>
            <div class="col-md-6 d-flex align-items-end">
              <label class="hms-toggle">
                <input type="checkbox" :checked="form.contrast_required == 1" @change="form.contrast_required = $event.target.checked ? 1 : 0" />
                <span>Contrast required</span>
              </label>
            </div>
            <div class="col-12"><label class="hms-label">Clinical Indication</label>
              <textarea class="hms-input" rows="3" v-model="form.clinical_indication" placeholder="Why is this scan being requested?"></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveRequest" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Creating...' : 'Create Request' }}
          </button>
        </div>
      </div>

      <!-- SCHEDULE MODAL -->
      <div class="hms-modal-overlay" v-if="showSchedule" @click.self="showSchedule=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-calendar-plus"></i> Schedule Scan</span>
            <button class="hms-modal-close" @click="showSchedule=false">&times;</button>
          </div>
          <div v-if="active"><span class="hms-mono">{{ active.request_number }}</span> · {{ active.scan_type }}</div>
          <div class="row g-3 my-3">
            <div class="col-12"><label class="hms-label">Scheduled Date / Time *</label>
              <input type="datetime-local" class="hms-input" v-model="scheduleAt" />
            </div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="confirmSchedule" :disabled="processing || !scheduleAt">Schedule</button>
        </div>
      </div>

      <!-- PERFORM MODAL -->
      <div class="hms-modal-overlay" v-if="showPerform" @click.self="showPerform=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-check-decagram"></i> Mark Performed</span>
            <button class="hms-modal-close" @click="showPerform=false">&times;</button>
          </div>
          <div v-if="active"><span class="hms-mono">{{ active.request_number }}</span> · {{ active.scan_type }}</div>
          <div class="row g-3 my-3">
            <div class="col-12"><label class="hms-label">Scan images / documents</label>
              <input ref="radFileInput" type="file" multiple style="display:none"
                     accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,.dcm"
                     @change="onRadFilesPicked" />
              <button class="hms-btn hms-btn-blue" :disabled="uploadingFiles" @click="$refs.radFileInput.click()">
                <span v-if="uploadingFiles" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="mdi mdi-upload me-1"></i>{{ uploadingFiles ? 'Uploading…' : 'Upload files' }}
              </button>
              <div class="hms-muted-sm mt-1">PNG / JPG / GIF / WEBP / PDF / DICOM, max 10 MB each. Optional.</div>
              <div v-if="uploadedFiles.length" class="hms-image-list mt-2">
                <span v-for="(f,i) in uploadedFiles" :key="i" class="hms-image-link">
                  <i class="mdi mdi-file-check-outline"></i>{{ f.name }}
                </span>
              </div>
            </div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="confirmPerform" :disabled="processing || uploadingFiles">Mark Performed</button>
        </div>
      </div>

      <!-- REPORT MODAL -->
      <div class="hms-modal-overlay" v-if="showReport" @click.self="showReport=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-file-document-edit"></i> Radiology Report</span>
            <button class="hms-modal-close" @click="showReport=false">&times;</button>
          </div>

          <div v-if="active" class="hms-kv mb-3">
            <div><span class="hms-kv-key">Request</span><span class="hms-mono">{{ active.request_number }}</span></div>
            <div><span class="hms-kv-key">Patient</span><span>{{ active.patient_first }} {{ active.patient_last }}</span></div>
            <div><span class="hms-kv-key">Scan</span><span>{{ active.scan_type }} · {{ active.body_part || '—' }}</span></div>
            <div v-if="active.clinical_indication"><span class="hms-kv-key">Indication</span><span>{{ active.clinical_indication }}</span></div>
          </div>

          <div class="hms-section-label">RAW FINDINGS (for AI draft)</div>
          <textarea class="hms-input mb-2" rows="3" v-model="rawFindings" placeholder="Brief findings observed during the scan — used as input for the AI draft."></textarea>
          <button class="hms-btn hms-btn-blue mb-3" @click="generateDraft" :disabled="processing">
            <i class="mdi mdi-robot-outline me-1"></i>{{ aiLoading ? 'Generating...' : 'AI Draft Report' }}
          </button>

          <div class="hms-section-label">STANDARD TEMPLATE</div>
          <div class="d-flex gap-2 mb-2">
            <select class="hms-input" v-model="selectedTemplateId" style="flex:1;">
              <option value="">— Choose a configured template —</option>
              <option v-for="t in matchingTemplates" :key="t.template_id" :value="t.template_id">
                {{ t.template_name }}{{ t.scan_type ? ' (' + t.scan_type + (t.body_part ? ' · ' + t.body_part : '') + ')' : '' }}
              </option>
            </select>
            <button class="hms-btn hms-btn-blue" @click="applyTemplate" :disabled="!selectedTemplateId">
              <i class="mdi mdi-import me-1"></i>Insert
            </button>
          </div>

          <div class="hms-section-label">REPORT</div>
          <textarea class="hms-input" rows="14" v-model="reportText" placeholder="Use sections: Technique / Findings / Impression / Recommendations"></textarea>

          <div class="d-flex justify-content-center mt-3">
            <SaveAndQueue ref="sqReport" from-point="radiology" :saving="processing" :disabled="!reportText"
                          save-label="Save Report" saving-label="Saving..."
                          btn-class="hms-btn hms-btn-primary" icon="mdi-content-save" @save="confirmReport" />
          </div>
        </div>
      </div>

      <!-- DETAIL MODAL -->
      <div class="hms-modal-overlay" v-if="showDetail && detail" @click.self="showDetail=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-radioactive"></i> {{ detail.request_number }}
              <span class="hms-badge ms-2" :class="statusBadge(detail.status)">{{ detail.status }}</span>
              <span class="hms-badge ms-1" :class="priorityBadge(detail.priority)">{{ detail.priority }}</span>
            </span>
            <button class="hms-modal-close" @click="showDetail=false">&times;</button>
          </div>

          <UnpaidBanner :patient-id="detail.patient_id" />

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Patient</span><span>{{ detail.patient_first }} {{ detail.patient_last }} <span class="hms-muted-sm">· {{ detail.patient_number }}</span></span></div>
            <div><span class="hms-kv-key">Scan</span><span>{{ detail.scan_type }} · {{ detail.body_part || '—' }}{{ detail.contrast_required == 1 ? ' · contrast' : '' }}</span></div>
            <div v-if="detail.clinical_indication"><span class="hms-kv-key">Indication</span><span>{{ detail.clinical_indication }}</span></div>
            <div><span class="hms-kv-key">Requested</span><span>{{ detail.req_first }} {{ detail.req_last }} · {{ formatDateTime(detail.created_at) }}</span></div>
            <div v-if="detail.scheduled_date"><span class="hms-kv-key">Scheduled</span><span>{{ formatDateTime(detail.scheduled_date) }}</span></div>
            <div v-if="detail.performed_date"><span class="hms-kv-key">Performed</span><span>{{ detail.perf_first }} {{ detail.perf_last }} · {{ formatDateTime(detail.performed_date) }}</span></div>
            <div v-if="detail.report_date"><span class="hms-kv-key">Reported</span><span>{{ detail.rep_first }} {{ detail.rep_last }} · {{ formatDateTime(detail.report_date) }}</span></div>
            <div><span class="hms-kv-key">Price</span><span>KSh {{ formatNumber(detail.price) }}</span></div>
          </div>

          <div v-if="detail.image_urls" class="mb-3">
            <div class="hms-section-label">IMAGES</div>
            <div class="hms-image-list">
              <a v-for="(u,i) in (detail.image_urls || '').split('\n').filter(x=>x.trim())" :key="i" :href="fileHref(u)" target="_blank" class="hms-image-link">
                <i class="mdi mdi-link-variant"></i>{{ u }}
              </a>
            </div>
          </div>

          <div v-if="detail.report" class="mb-2">
            <div class="hms-section-label">REPORT</div>
            <div class="hms-report-box">{{ detail.report }}</div>
          </div>
        </div>
      </div>

      <!-- TEMPLATES MODAL -->
      <div class="hms-modal-overlay" v-if="showTemplates" @click.self="showTemplates=false">
        <div class="hms-modal" style="max-width:760px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-file-document-multiple-outline"></i> Radiology Report Templates</span>
            <button class="hms-modal-close" @click="showTemplates=false">&times;</button>
          </div>
          <p class="hms-muted-sm mb-3">Configure standard report texts so radiologists choose from a pre-approved list instead of typing results from scratch.</p>

          <div v-if="templates.length" class="mb-3" style="max-height:220px;overflow-y:auto;">
            <table class="hms-table">
              <thead><tr><th>Name</th><th>Scan</th><th>Body part</th><th>Status</th><th></th></tr></thead>
              <tbody>
                <tr v-for="t in templates" :key="t.template_id">
                  <td>{{ t.template_name }}</td>
                  <td>{{ t.scan_type || '—' }}</td>
                  <td>{{ t.body_part || '—' }}</td>
                  <td><span class="hms-badge" :class="t.status==='active'?'hms-badge-success':'hms-badge-neutral'">{{ t.status }}</span></td>
                  <td class="text-end">
                    <button class="hms-btn hms-btn-sm hms-btn-blue" @click="editTemplate(t)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                    <button class="hms-btn hms-btn-sm hms-btn-danger ms-1" @click="deleteTemplate(t)" title="Delete"><i class="mdi mdi-delete"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-muted mb-3">No templates yet. Add the first one below.</div>

          <div class="hms-section-label">{{ tplForm.template_id ? 'EDIT TEMPLATE' : 'ADD TEMPLATE' }}</div>
          <div class="row g-2 mb-2">
            <div class="col-md-4"><input class="hms-input" v-model="tplForm.template_name" placeholder="Template name *" /></div>
            <div class="col-md-4">
              <select class="hms-input" v-model="tplForm.scan_type">
                <option value="">Any scan type</option>
                <option v-for="s in scanTypes" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <div class="col-md-4"><input class="hms-input" v-model="tplForm.body_part" placeholder="Body part (optional)" /></div>
          </div>
          <textarea class="hms-input mb-2" rows="8" v-model="tplForm.template_text"
            placeholder="Standard report text, e.g.&#10;TECHNIQUE: ...&#10;FINDINGS: ...&#10;IMPRESSION: ..."></textarea>
          <div class="d-flex align-items-center gap-2">
            <select class="hms-input" v-model="tplForm.status" style="max-width:140px;">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button v-if="tplForm.template_id" class="hms-btn hms-btn-outline" @click="tplForm = emptyTplForm()">Cancel edit</button>
            <button class="hms-btn hms-btn-primary ms-auto" @click="saveTemplate" :disabled="processing || !tplForm.template_name || !tplForm.template_text">
              <i class="mdi mdi-content-save me-1"></i>{{ tplForm.template_id ? 'Update template' : 'Add template' }}
            </button>
          </div>
          <div v-if="tplErr" class="hms-toast-error mt-2" style="font-size:13px;color:#dc2626;">{{ tplErr }}</div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 130px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-amber { color: var(--hms-warning); }
.hms-stat-info  { color: var(--hms-info); }
.hms-stat-green { color: var(--hms-success); }
.hms-stat-red   { color: var(--hms-danger); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-top: 4px; margin-bottom: 8px; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: top; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-info { background: rgba(23,162,184,.15); color: var(--hms-info); border: 1px solid rgba(23,162,184,.3); }
.hms-btn-info:hover:not(:disabled) { background: rgba(23,162,184,.25); }
.hms-btn-green { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-green:hover:not(:disabled) { background: rgba(40,167,69,.25); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: rgba(220,53,69,.25); }

.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-sm { max-width: 460px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 140px; font-weight: 600; }

.hms-image-list { display: flex; flex-direction: column; gap: 4px; }
.hms-image-link { font-size: 12px; color: var(--hms-primary); text-decoration: none; word-break: break-all; }
.hms-image-link i { margin-right: 4px; }

.hms-report-box { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 14px 16px; white-space: pre-wrap; color: var(--hms-h); font-size: 13px; max-height: 400px; overflow-y: auto; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import SaveAndQueue from "@/components/hms-shared/SaveAndQueue.vue";
import UnpaidBanner from "@/components/hms-shared/UnpaidBanner.vue";
import { executeJson, hms, base } from "@/api";

const SCAN_TYPES = [
  "X-Ray", "Ultrasound", "CT Scan", "MRI", "Mammography",
  "Fluoroscopy", "Doppler", "Echocardiogram", "DEXA Bone Density",
  "PET Scan", "Angiography", "Nuclear Medicine"
];

export default {
  name: "RadiologyManagement",
  components: { Navigation, SaveAndQueue, UnpaidBanner },
  data() {
    return {
      loading: true, processing: false, aiLoading: false,
      requests: [], patients: [],
      scanTypes: SCAN_TYPES,

      search: "", filterStatus: "", filterPriority: "",

      // Modals
      showAdd: false, showSchedule: false, showPerform: false, showReport: false, showDetail: false,
      showTemplates: false,
      active: null, detail: null,

      // Forms
      form: this.emptyForm(), formError: "",
      scheduleAt: "",
      imageUrls: "",
      uploadingFiles: false, uploadedFiles: [],
      reportText: "", rawFindings: "",
      templates: [], selectedTemplateId: "",
      tplForm: this.emptyTplForm(), tplErr: "",

      toastMsg: "", toastError: false, toastTimer: null
    };
  },
  computed: {
    statCount() { return this.requests.filter(r => r.priority === 'stat' || r.priority === 'urgent').length; },
    filteredRequests() {
      var list = this.requests;
      if (this.filterStatus)   list = list.filter(r => r.status === this.filterStatus);
      if (this.filterPriority) list = list.filter(r => r.priority === this.filterPriority);
      var q = (this.search || "").trim().toLowerCase();
      if (q) {
        list = list.filter(r =>
          ((r.request_number||"") + " " + (r.scan_type||"") + " " + (r.body_part||"") + " " + (r.patient_first||"") + " " + (r.patient_last||"") + " " + (r.patient_number||""))
            .toLowerCase().indexOf(q) >= 0);
      }
      return list;
    },
    matchingTemplates() {
      var act = this.active || {};
      var scan = (act.scan_type || "").toLowerCase();
      return this.templates.filter(t => {
        if (t.status !== 'active') return false;
        if (!t.scan_type) return true;
        return String(t.scan_type).toLowerCase() === scan;
      });
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyForm() { return { patient_id:"", scan_type:"", body_part:"", clinical_indication:"", contrast_required:0, priority:"routine", scheduled_date:"", price:0 }; },
    emptyTplForm() { return { template_id: 0, template_name: "", scan_type: "", body_part: "", template_text: "", status: "active" }; },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },
    formatNumber(n) { if (n===null||n===undefined||n==="") return "0"; return Number(n).toLocaleString(); },
    formatDate(s) { if (!s) return "—"; var d = new Date(s.replace ? s.replace(" ","T") : s); return isNaN(d) ? s : d.toLocaleDateString(); },
    formatDateTime(s) { if (!s) return "—"; var d = new Date(s.replace ? s.replace(" ","T") : s); return isNaN(d) ? s : d.toLocaleString(); },
    countByStatus(s) { return this.requests.filter(r => r.status === s).length; },
    statusColor(s) { return ({ pending:'hms-stat-amber', scheduled:'hms-stat-info', performed:'hms-stat-amber', reported:'hms-stat-green' })[s] || ''; },
    statusBadge(s) { return ({ pending:'hms-badge-warning', scheduled:'hms-badge-info', performed:'hms-badge-purple', reported:'hms-badge-success', cancelled:'hms-badge-neutral' })[s] || 'hms-badge-neutral'; },
    priorityBadge(p) { return ({ routine:'hms-badge-neutral', urgent:'hms-badge-warning', stat:'hms-badge-danger' })[p] || 'hms-badge-neutral'; },

    async loadAll() {
      this.loading = true;
      try {
        var res = await Promise.all([
          executeJson({ function: "getRadiologyRequests" }, hms),
          executeJson({ function: "getPatients" }, hms),
          executeJson({ function: "getRadiologyTemplates" }, hms)
        ]);
        if (res[0].data && res[0].data.success) this.requests = (res[0].data.data && res[0].data.data.Requests) || [];
        if (res[1].data && res[1].data.success) this.patients = (res[1].data.data && res[1].data.data.Patients) || [];
        if (res[2].data && res[2].data.success) this.templates = (res[2].data.data && res[2].data.data.Templates) || [];
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    openAdd() { this.form = this.emptyForm(); this.formError = ""; this.showAdd = true; },
    async saveRequest() {
      this.formError = "";
      if (!this.form.patient_id || !this.form.scan_type) { this.formError = "Patient and scan type are required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createRadiologyRequest" }, this.form), hms);
        if (r.data && r.data.success) { this.showAdd = false; this.showToast("Request created"); await this.loadAll(); }
        else { this.formError = r.data.message || "Failed."; }
      } catch(e) { this.formError = "Connection error."; }
      this.processing = false;
    },

    openSchedule(r) { this.active = r; this.scheduleAt = ""; this.showSchedule = true; },
    async confirmSchedule() {
      if (!this.scheduleAt) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "scheduleRadiology", radiology_id: this.active.radiology_id, scheduled_date: this.scheduleAt }, hms);
        if (r.data && r.data.success) { this.showSchedule = false; this.showToast("Scheduled"); await this.loadAll(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openPerform(r) {
      this.active = r;
      this.imageUrls = r.image_urls || "";
      this.uploadedFiles = [];
      this.showPerform = true;
    },
    onRadFilesPicked(e) {
      var files = Array.from(e.target.files || []);
      e.target.value = "";
      if (!files.length) return;
      var self = this;
      this.uploadingFiles = true;
      var chain = Promise.resolve();
      files.forEach(function(file) {
        chain = chain.then(function() {
          if (file.size > 10 * 1024 * 1024) { self.showToast(file.name + " is over 10 MB", true); return; }
          return new Promise(function(resolve) {
            var reader = new FileReader();
            reader.onload = async function() {
              try {
                var r = await executeJson({
                  function: "uploadRadiologyImage",
                  radiology_id: self.active.radiology_id,
                  file_base64: reader.result,
                  file_name: file.name
                }, hms);
                if (r.data && r.data.success) {
                  var url = r.data.data.file_url;
                  self.uploadedFiles.push({ name: file.name, url: url });
                  self.imageUrls = (self.imageUrls ? self.imageUrls.replace(/\s+$/, "") + "\n" : "") + url;
                } else {
                  self.showToast(file.name + ": " + (r.data.message || "upload failed"), true);
                }
              } catch(err) { self.showToast(file.name + ": connection error", true); }
              resolve();
            };
            reader.readAsDataURL(file);
          });
        });
      });
      chain.then(function() { self.uploadingFiles = false; });
    },
    fileHref(u) {
      if (/^https?:\/\//i.test(u)) return u;
      return (base || "").replace(/\/$/, "") + "/" + String(u).replace(/^\//, "");
    },
    async confirmPerform() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "performRadiology", radiology_id: this.active.radiology_id, image_urls: this.imageUrls }, hms);
        if (r.data && r.data.success) { this.showPerform = false; this.showToast("Marked performed"); await this.loadAll(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openReport(r) { this.active = r; this.reportText = r.report || ""; this.rawFindings = ""; this.selectedTemplateId = ""; this.showReport = true; },
    applyTemplate() {
      var t = this.templates.find(x => String(x.template_id) === String(this.selectedTemplateId));
      if (!t) return;
      this.reportText = this.reportText ? (this.reportText.replace(/\s+$/, "") + "\n\n" + t.template_text) : t.template_text;
      this.showToast("Template inserted — edit findings before saving");
    },
    openTemplates() { this.tplForm = this.emptyTplForm(); this.tplErr = ""; this.showTemplates = true; },
    editTemplate(t) {
      this.tplForm = {
        template_id: t.template_id, template_name: t.template_name,
        scan_type: t.scan_type || "", body_part: t.body_part || "",
        template_text: t.template_text, status: t.status || "active"
      };
      this.tplErr = "";
    },
    async saveTemplate() {
      this.tplErr = "";
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "saveRadiologyTemplate" }, this.tplForm), hms);
        if (r.data && r.data.success) {
          this.tplForm = this.emptyTplForm();
          this.showToast("Template saved");
          var res = await executeJson({ function: "getRadiologyTemplates" }, hms);
          if (res.data && res.data.success) this.templates = (res.data.data && res.data.data.Templates) || [];
        } else { this.tplErr = r.data.message || "Failed."; }
      } catch(e) { this.tplErr = "Connection error."; }
      this.processing = false;
    },
    async deleteTemplate(t) {
      if (!confirm('Delete template "' + t.template_name + '"?')) return;
      try {
        var r = await executeJson({ function: "deleteRadiologyTemplate", template_id: t.template_id }, hms);
        if (r.data && r.data.success) {
          this.templates = this.templates.filter(x => x.template_id !== t.template_id);
          this.showToast("Template removed");
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },
    async generateDraft() {
      if (this.aiLoading) return;
      this.aiLoading = true;
      try {
        var r = await executeJson({
          function: "aiRadiologyReport",
          scan_type: this.active.scan_type, body_part: this.active.body_part,
          clinical_indication: this.active.clinical_indication, findings: this.rawFindings
        }, hms);
        if (r.data && r.data.success && r.data.data && r.data.data.draft) {
          this.reportText = r.data.data.draft;
          this.showToast("AI draft generated — verify before signing");
        } else {
          this.showToast(r.data.message || "AI failed", true);
        }
      } catch(e) { this.showToast("Connection error", true); }
      this.aiLoading = false;
    },
    async confirmReport() {
      if (!this.reportText) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "reportRadiology", radiology_id: this.active.radiology_id, report: this.reportText }, hms);
        if (r.data && r.data.success) {
          this.showToast("Report saved");
          // If radiologist picked a "Save & queue to ___", route the patient now.
          var pid = this.active && this.active.patient_id;
          if (this.$refs.sqReport && pid) {
            var q = await this.$refs.sqReport.queueIfSelected(pid);
            if (q && q.ok) this.showToast("Queued to " + q.dept_name + " (ticket " + q.ticket_number + ")");
          }
          this.showReport = false;
          await this.loadAll();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openDetail(r) { this.detail = r; this.showDetail = true; },

    async cancelRequest(r) {
      if (!confirm("Cancel request " + r.request_number + "?")) return;
      try {
        var resp = await executeJson({ function: "cancelRadiology", radiology_id: r.radiology_id }, hms);
        if (resp.data && resp.data.success) { this.showToast("Cancelled"); await this.loadAll(); }
        else { this.showToast(resp.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    }
  }
};
</script>
